import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiBarChart2,
    FiCalendar,
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchGstDashboard,

    gstDashboardSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../store/billing/gst/gstReportSlice";
import HsnSummaryReport
    from "./components/HsnSummaryReport";
import GstFilingPanel
    from "./components/GstFilingPanel";
/*
|--------------------------------------------------------------------------
| COMPONENTS
|--------------------------------------------------------------------------
*/

import SalesGstRegister
    from "./components/SalesGstRegister";

import PurchaseGstRegister
    from "./components/PurchaseGstRegister";

import Gstr1Report
    from "./components/Gstr1Report";

import Gstr3bReport
    from "./components/Gstr3bReport";


import "./GstDashboard.css";
import GstReconciliationReport
    from "./components/GstReconciliationReport";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        toNumber(value)
    );


const getTodayInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() +
                1
            ).padStart(
                2,
                "0"
            );

        const day =
            String(
                date.getDate()
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-${day}`;
    };


const getMonthStartInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() +
                1
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-01`;
    };


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const GstDashboardPage =
    () => {
        const dispatch =
            useDispatch();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const dashboard =
            useSelector(
                gstDashboardSelector
            ) || {};

        const loading =
            useSelector(
                gstReportLoadingSelector
            ) || {};

        const errors =
            useSelector(
                gstReportErrorSelector
            ) || {};


        /*
        |--------------------------------------------------------------------------
        | ACTIVE TAB
        |--------------------------------------------------------------------------
        */

        const [
            activeTab,
            setActiveTab,
        ] = useState(
            "DASHBOARD"
        );


        /*
        |--------------------------------------------------------------------------
        | COMMON GST PERIOD
        |--------------------------------------------------------------------------
        */

        const [
            fromDate,
            setFromDate,
        ] = useState(
            getMonthStartInputValue()
        );

        const [
            toDate,
            setToDate,
        ] = useState(
            getTodayInputValue()
        );

        const [
            financialYear,
            setFinancialYear,
        ] = useState("");


        /*
        |--------------------------------------------------------------------------
        | TABS
        |--------------------------------------------------------------------------
        */

        const tabs =
            useMemo(
                () => [
                    {
                        key:
                            "DASHBOARD",

                        label:
                            "Dashboard",
                    },

                    {
                        key:
                            "SALES_REGISTER",

                        label:
                            "Sales Register",
                    },

                    {
                        key:
                            "PURCHASE_REGISTER",

                        label:
                            "Purchase Register",
                    },

                    {
                        key:
                            "GSTR1",

                        label:
                            "GSTR-1",
                    },

                    {
                        key:
                            "GSTR3B",

                        label:
                            "GSTR-3B",
                    },

                    {
                        key:
                            "HSN_SUMMARY",

                        label:
                            "HSN Summary",
                    },

                    {
                        key:
                            "RECONCILIATION",

                        label:
                            "Reconciliation",
                    },
                    {
                        key:
                            "FILING",

                        label:
                            "Filing & Export",
                    },
                ],
                []
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD DASHBOARD
        |--------------------------------------------------------------------------
        */

        const loadDashboard =
            useCallback(
                async () => {
                    if (
                        activeTab !==
                        "DASHBOARD"
                    ) {
                        return;
                    }

                    await dispatch(
                        fetchGstDashboard({
                            fromDate,
                            toDate,
                            financialYear,
                        })
                    );
                },
                [
                    dispatch,
                    activeTab,
                    fromDate,
                    toDate,
                    financialYear,
                ]
            );


        useEffect(
            () => {
                loadDashboard();
            },
            [
                loadDashboard,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | DASHBOARD DATA
        |--------------------------------------------------------------------------
        */

        const purchase =
            dashboard?.purchase ||
            {};

        const sales =
            dashboard?.sales ||
            {};

        const taxPosition =
            dashboard?.taxPosition ||
            {};


        /*
        |--------------------------------------------------------------------------
        | DASHBOARD CARDS
        |--------------------------------------------------------------------------
        */

        const cards =
            useMemo(
                () => [
                    {
                        label:
                            "Output GST",

                        value:
                            formatCurrency(
                                taxPosition
                                    ?.outputTax
                            ),

                        help:
                            "GST collected on sales",
                    },

                    {
                        label:
                            "Input GST",

                        value:
                            formatCurrency(
                                taxPosition
                                    ?.inputTax
                            ),

                        help:
                            "Eligible GST on purchases",
                    },

                    {
                        label:
                            "Net Tax Payable",

                        value:
                            formatCurrency(
                                taxPosition
                                    ?.netTaxPayable
                            ),

                        help:
                            "Output GST less Input GST",
                    },

                    {
                        label:
                            "Excess ITC",

                        value:
                            formatCurrency(
                                taxPosition
                                    ?.excessItc
                            ),

                        help:
                            "Input GST exceeding Output GST",
                    },
                ],
                [
                    taxPosition,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | RESET PERIOD
        |--------------------------------------------------------------------------
        */

        const handleResetPeriod =
            () => {
                setFromDate(
                    getMonthStartInputValue()
                );

                setToDate(
                    getTodayInputValue()
                );

                setFinancialYear(
                    ""
                );
            };


        /*
        |--------------------------------------------------------------------------
        | TAB CHANGE
        |--------------------------------------------------------------------------
        */

        const handleTabChange =
            (
                tabKey
            ) => {
                setActiveTab(
                    tabKey
                );
            };


        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="gst-dashboard-page">

                {/* =====================================================
                    HEADER
                ===================================================== */}

                <header className="gst-dashboard-header">

                    <div>

                        <small>
                            GST COMPLIANCE
                        </small>

                        <h1>
                            GST Reports & Filing
                        </h1>

                        <p>
                            Review sales GST, purchase GST, input tax credit,
                            GSTR summaries and filing data.
                        </p>

                    </div>


                    <div className="gst-dashboard-header-actions">

                        {activeTab ===
                            "DASHBOARD" && (

                                <button
                                    type="button"
                                    className="gst-dashboard-button"
                                    disabled={
                                        loading.dashboard
                                    }
                                    onClick={
                                        loadDashboard
                                    }
                                >

                                    <FiRefreshCw
                                        className={
                                            loading.dashboard
                                                ? "gst-dashboard-spinner"
                                                : ""
                                        }
                                    />

                                    Refresh

                                </button>

                            )}

                    </div>

                </header>


                {/* =====================================================
                    TABS
                ===================================================== */}

                <div className="gst-dashboard-tabs">

                    {tabs.map(
                        (
                            tab
                        ) => (

                            <button
                                key={
                                    tab.key
                                }
                                type="button"
                                className={
                                    activeTab ===
                                        tab.key
                                        ? "active"
                                        : ""
                                }
                                onClick={() =>
                                    handleTabChange(
                                        tab.key
                                    )
                                }
                            >
                                {tab.label}
                            </button>

                        )
                    )}

                </div>


                {/* =====================================================
                    COMMON GST PERIOD
                ===================================================== */}

                <section className="gst-dashboard-period">

                    <div className="gst-dashboard-period-title">

                        <FiCalendar />

                        <div>

                            <strong>
                                GST Period
                            </strong>

                            <span>
                                Applied across GST reports
                            </span>

                        </div>

                    </div>


                    <div className="gst-dashboard-period-fields">

                        <div className="gst-dashboard-field">

                            <label>
                                From Date
                            </label>

                            <input
                                type="date"
                                value={
                                    fromDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    setFromDate(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />

                        </div>


                        <div className="gst-dashboard-field">

                            <label>
                                To Date
                            </label>

                            <input
                                type="date"
                                value={
                                    toDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    setToDate(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />

                        </div>


                        <div className="gst-dashboard-field">

                            <label>
                                Financial Year
                            </label>

                            <input
                                type="text"
                                value={
                                    financialYear
                                }
                                placeholder="2026-27"
                                onChange={(
                                    event
                                ) =>
                                    setFinancialYear(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />

                        </div>


                        <div className="gst-dashboard-field gst-dashboard-field-action">

                            <button
                                type="button"
                                className="gst-dashboard-button"
                                onClick={
                                    handleResetPeriod
                                }
                            >
                                Reset
                            </button>

                        </div>

                    </div>

                </section>


                {/* =====================================================
                    DASHBOARD
                ===================================================== */}

                {activeTab ===
                    "DASHBOARD" && (

                        <>

                            {errors.dashboard && (

                                <div className="gst-dashboard-alert">

                                    <FiAlertCircle />

                                    <span>
                                        {errors.dashboard}
                                    </span>

                                </div>

                            )}


                            <section className="gst-dashboard-summary-grid">

                                {cards.map(
                                    (
                                        card
                                    ) => (

                                        <div
                                            key={
                                                card.label
                                            }
                                            className="gst-dashboard-summary-card"
                                        >

                                            <span>
                                                {card.label}
                                            </span>

                                            <strong>
                                                {card.value}
                                            </strong>

                                            <small>
                                                {card.help}
                                            </small>

                                        </div>

                                    )
                                )}

                            </section>


                            {/* SALES GST */}

                            <section className="gst-dashboard-section">

                                <div className="gst-dashboard-section-header">

                                    <div>

                                        <FiBarChart2 />

                                        <span>
                                            Sales GST
                                        </span>

                                    </div>

                                </div>


                                <div className="gst-dashboard-stat-grid">

                                    <div>
                                        <span>
                                            Documents
                                        </span>

                                        <strong>
                                            {toNumber(
                                                sales?.totalDocuments
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            Taxable
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                sales?.taxableAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            CGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                sales?.cgstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            SGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                sales?.sgstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            IGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                sales?.igstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            Output Tax
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                sales?.outputTax
                                            )}
                                        </strong>
                                    </div>

                                </div>

                            </section>


                            {/* PURCHASE GST */}

                            <section className="gst-dashboard-section">

                                <div className="gst-dashboard-section-header">

                                    <div>

                                        <FiFileText />

                                        <span>
                                            Purchase GST / ITC
                                        </span>

                                    </div>

                                </div>


                                <div className="gst-dashboard-stat-grid">

                                    <div>
                                        <span>
                                            Documents
                                        </span>

                                        <strong>
                                            {toNumber(
                                                purchase?.totalDocuments
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            Taxable
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase?.taxableAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            CGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase?.cgstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            SGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase?.sgstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            IGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase?.igstAmount
                                            )}
                                        </strong>
                                    </div>


                                    <div>
                                        <span>
                                            Input Tax
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase?.inputTax
                                            )}
                                        </strong>
                                    </div>

                                </div>

                            </section>


                            {/* RCM */}

                            <section className="gst-dashboard-section">

                                <div className="gst-dashboard-section-header">

                                    <div>

                                        <FiFileText />

                                        <span>
                                            Reverse Charge
                                        </span>

                                    </div>

                                </div>


                                <div className="gst-dashboard-stat-grid">

                                    <div>

                                        <span>
                                            RCM Taxable
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase
                                                    ?.reverseChargeTaxableAmount
                                            )}
                                        </strong>

                                    </div>


                                    <div>

                                        <span>
                                            RCM Tax
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchase
                                                    ?.reverseChargeTaxAmount
                                            )}
                                        </strong>

                                    </div>

                                </div>

                            </section>

                        </>

                    )}


                {/* =====================================================
                    SALES REGISTER
                ===================================================== */}

                {activeTab ===
                    "SALES_REGISTER" && (

                        <SalesGstRegister
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}


                {/* =====================================================
                    PURCHASE REGISTER
                ===================================================== */}

                {activeTab ===
                    "PURCHASE_REGISTER" && (

                        <PurchaseGstRegister
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}


                {/* =====================================================
                    GSTR-1
                ===================================================== */}

                {activeTab ===
                    "GSTR1" && (

                        <Gstr1Report
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}


                {/* =====================================================
                    GSTR-3B
                ===================================================== */}

                {activeTab ===
                    "GSTR3B" && (

                        <Gstr3bReport
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}


                {/* =====================================================
                    HSN SUMMARY
                ===================================================== */}

                {activeTab ===
                    "HSN_SUMMARY" && (

                        <HsnSummaryReport
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}


                {/* =====================================================
                    RECONCILIATION
                ===================================================== */}

                {activeTab ===
                    "RECONCILIATION" && (

                        <GstReconciliationReport
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}
                {activeTab ===
                    "FILING" && (

                        <GstFilingPanel
                            fromDate={
                                fromDate
                            }

                            toDate={
                                toDate
                            }

                            financialYear={
                                financialYear
                            }
                        />

                    )}

            </div>
        );
    };


export default GstDashboardPage;